/**
 * @fileoverview Secresea host service for the **view-only Orchard FROST
 * co-signing wallet**.
 *
 * Loads ONE WINBIT32 `.wult` share, derives the FROST UFVK/unified address
 * (the spend authority is the threshold key — no seed lives here), scans the
 * UFVK view-only for balance/notes, and drives a shielded send by co-signing
 * each Orchard action with a WINBIT32 cosigner over the WB32COSIGN relay.
 *
 * This is the host glue layer: it wires the framework-agnostic `cosign/` module
 * (load/derive/relay/ceremony/send-orchestration) to Secresea's infrastructure
 * — the `@shared/zcash` wallet-scanner client (view-only `scan-ufvk/job`), the
 * WebZjs `SeedFingerprint` WASM (PCZT `zip32_derivation` metadata), and the
 * NFPT PCZT + relay endpoints. Everything external is dependency-injected so
 * the whole service is unit-testable without a network, chain, or heavy WASM.
 *
 * Pipeline:  load share → derive UFVK/UA + seed fingerprint → scan notes
 *            (witness) → select + map to spends → POST /pczt/build →
 *            cosignSendPczt (QR pair → per-action FROST ceremony → apply →
 *            broadcast) → txid.
 */

import {
	DEFAULT_NETWORK,
	ZATOSHIS_PER_ZEC,
	configureWasm,
	getWebZjsClient,
	createWalletScannerClient,
	type WalletScannerClientType,
} from '@shared/zcash/index.ts';
import * as webzjsWasm from '@shared/zcash/wasm/webzjs_keys_and_send_fixed.js';
import {
	resolveCosignConfig,
	unwrapVaultShare,
	loadOrchardFrostWasm,
	deriveOrchardAddressFromBundle,
	deriveZcashSeedFromVaultIdentity,
	createCosignSession,
	createDefaultTransports,
	cosignSendPczt,
	buildPczt,
	endCosignSession,
	bytesToHex,
	type CosignConfig,
	type OrchardFrostWasmModule,
	type OrchardKeyBundle,
	type OrchardKeyShare,
	type CosignCallbacks,
	type CosignTransport,
} from './cosign';

// ─── Constants ──────────────────────────────────────────────────────

/** Orchard note commitment tree depth — a full witness has this many siblings. */
export const ORCHARD_MERKLE_DEPTH = 32;
/** ZIP-317 marginal fee (zatoshis per logical action). */
const MARGINAL_FEE_ZAT = 5000;
/** ZIP-317 grace actions (fee is charged for at least this many). */
const GRACE_ACTIONS = 2;
/** Default scanner API base (NFPT mounts wallet-scanner under /api). */
const DEFAULT_SCANNER_BASE_URL = '/api';
/** Scan-job poll interval. */
const SCAN_POLL_MS = 4000;
/** Scan-job overall timeout. */
const SCAN_TIMEOUT_MS = 10 * 60 * 1000;

// ─── Public shapes ──────────────────────────────────────────────────

export interface LoadedCosignWallet {
	ufvk: string;
	unifiedAddress: string;
	/** 32-byte ZIP-32 seed fingerprint (hex) for PCZT metadata, or null if no identity in the share. */
	seedFingerprintHex: string | null;
	/** This device's FROST identifier (hex). */
	identifier: string;
	minSigners: number;
	maxSigners: number;
}

export interface CosignBalance {
	balanceZatoshis: number;
	balanceZec: number;
	noteCount: number;
}

export interface CosignSendOptions {
	toAddress: string;
	amountZec: number;
	memo?: string;
	/** Optional account birthday to bound the scan. */
	birthdayHeight?: number;
	/** Fires with the WB32COSIGN QR payload once the session is created. */
	onQrReady?: (qrPayload: string) => void;
	callbacks?: CosignCallbacks;
}

export interface CosignSendResult {
	txid: string;
	signedPczt: string;
}

/** Raw note shape returned by the orchard-scanner (subset we consume). */
interface ScannedNote {
	commitment: string;
	value: number;
	nullifier?: string;
	note_nullifier?: string;
	rho?: string;
	anchor_height: number;
	anchor_root?: string;
	leaf_position?: number | string;
	merkle_path?: { siblings?: string[] };
	note_plaintext?: { diversifier?: string; pk_d?: string; rseed?: string; value?: number };
	block_height: number;
	[key: string]: unknown;
}

export interface CosignWalletDeps {
	/** Cosign module config overrides (PCZT/relay base URLs, network, fetch). */
	config?: Partial<CosignConfig>;
	/** Wallet-scanner base URL (defaults to `/api`). */
	scannerBaseUrl?: string;
	/** Optional X-API-Key for the scanner. */
	scannerApiKey?: string;
	/** Override the Orchard FROST WASM loader (tests / custom bundling). */
	loadWasm?: (config: CosignConfig) => Promise<OrchardFrostWasmModule>;
	/** Override seed-fingerprint computation (tests). Default uses WebZjs. */
	computeSeedFingerprint?: (seed64: Uint8Array) => Promise<string>;
	/** Override the wallet-scanner client (tests). */
	scannerClient?: WalletScannerClientType;
	/** Override the default transport factory (tests). */
	createTransport?: (sessionId: string, encKeyHex: string) => CosignTransport;
}

const HEX64 = /^[0-9a-fA-F]{64}$/;

const toZec = (zatoshis: number): number => zatoshis / ZATOSHIS_PER_ZEC;
const toZatoshis = (zec: number): number => Math.round(zec * ZATOSHIS_PER_ZEC);

/** ZIP-317 conventional fee for the given action counts. */
const estimateFeeZat = (numSpends: number, numOutputs: number): number =>
	MARGINAL_FEE_ZAT * Math.max(GRACE_ACTIONS, numSpends, numOutputs);

/**
 * Coerce the `.wult` orchard payload into a validated {@link OrchardKeyBundle}.
 * Throws a clear error if the share is missing the fields we need to co-sign.
 */
export const toOrchardBundle = (raw: unknown): OrchardKeyBundle => {
	const b = raw as Partial<OrchardKeyBundle> | null;
	const share = b?.myShare as Partial<OrchardKeyShare> | undefined;
	const pkg = b?.publicKeyPackage;
	if (!share?.id || !share.secretShare || !share.publicKey || !share.groupPublic) {
		throw new Error('This share has no usable Orchard FROST key (missing myShare fields).');
	}
	if (!pkg?.groupPublic || !pkg.signerPubkeys) {
		throw new Error('This share is missing its Orchard public key package.');
	}
	return b as OrchardKeyBundle;
};

/** Pick the first present, valid 64-hex value from a list of candidates. */
const firstHex64 = (...candidates: Array<unknown>): string | null => {
	for (const c of candidates) {
		if (typeof c === 'string' && HEX64.test(c)) return c.toLowerCase();
	}
	return null;
};

const extractSiblings = (note: ScannedNote): string[] => {
	const s = note.merkle_path?.siblings
		?? (note as any).merklePath?.siblings
		?? (note as any).merkle_path_siblings_hex
		?? (note as any).merklePathSiblingsHex;
	return Array.isArray(s) ? s : [];
};

/**
 * Map a scanned witness note → PCZT `SpendInput` (exact field names the
 * orchard-scanner `build-pczt` deserialises). Requires a full witness
 * (anchor root + leaf position + 32 siblings); throws otherwise so we never
 * submit an unspendable note.
 */
export const noteToSpend = (note: ScannedNote): Record<string, unknown> => {
	const diversifier = note.note_plaintext?.diversifier ?? (note as any).diversifier;
	const pkd = note.note_plaintext?.pk_d ?? (note as any).pk_d;
	const rseed = note.note_plaintext?.rseed ?? (note as any).rseed;
	const nullifierHex = firstHex64(note.note_nullifier, note.nullifier);
	const rhoHex = firstHex64(note.rho, (note as any).rho_hex);
	const anchorRootHex = firstHex64(note.anchor_root, (note as any).anchor_root_hex);
	const siblings = extractSiblings(note);
	const leafPosition = note.leaf_position ?? (note as any).leafPosition;

	if (!nullifierHex) throw new Error('Note is missing its nullifier — rescan with witness=true.');
	if (!rhoHex) throw new Error('Note is missing rho — rescan with an up-to-date scanner.');
	if (!anchorRootHex || leafPosition === undefined || siblings.length !== ORCHARD_MERKLE_DEPTH) {
		throw new Error('Note is missing a full Orchard witness (anchor/position/merkle path) — rescan with witness=true.');
	}

	return {
		noteCommitmentHex: note.commitment,
		diversifierHex: diversifier,
		pkdHex: pkd,
		rseedHex: rseed,
		rhoHex,
		valueZatoshis: note.value,
		anchorHeight: note.anchor_height,
		anchorRootHex,
		leafPosition: String(leafPosition),
		merklePathSiblingsHex: siblings,
		nullifierHex,
		blockHeight: note.block_height,
	};
};

/**
 * Greedily select notes (largest first) to cover `amountZat` plus the
 * (count-dependent) ZIP-317 fee. Returns the chosen notes and the fee used.
 */
export const selectNotes = (
	notes: ScannedNote[],
	amountZat: number,
): { selected: ScannedNote[]; feeZat: number } => {
	const sorted = [...notes].filter((n) => Number(n.value) > 0).sort((a, b) => b.value - a.value);
	const selected: ScannedNote[] = [];
	let sum = 0;
	for (const note of sorted) {
		selected.push(note);
		sum += Number(note.value);
		// One recipient output; assume a change output for the fee estimate.
		const feeZat = estimateFeeZat(selected.length, 2);
		if (sum >= amountZat + feeZat) return { selected, feeZat };
	}
	const have = toZec(sum).toFixed(8);
	const need = toZec(amountZat).toFixed(8);
	throw new Error(`Insufficient shielded funds: have ${have} ZEC, need ${need} ZEC plus fee.`);
};

// ─── Service ────────────────────────────────────────────────────────

export class CosignWalletService {
	private readonly config: Readonly<CosignConfig>;
	private readonly deps: CosignWalletDeps;
	private wasm: OrchardFrostWasmModule | null = null;
	private bundle: OrchardKeyBundle | null = null;
	private loaded: LoadedCosignWallet | null = null;
	private webzjsReady = false;

	constructor(deps: CosignWalletDeps = {}) {
		this.deps = deps;
		this.config = resolveCosignConfig(deps.config);
	}

	getWallet(): LoadedCosignWallet | null {
		return this.loaded;
	}

	isLoaded(): boolean {
		return this.loaded !== null;
	}

	private async ensureWasm(): Promise<OrchardFrostWasmModule> {
		if (!this.wasm) {
			this.wasm = await (this.deps.loadWasm ?? loadOrchardFrostWasm)(this.config);
		}
		return this.wasm;
	}

	private async computeSeedFingerprint(seed64: Uint8Array): Promise<string> {
		if (this.deps.computeSeedFingerprint) return this.deps.computeSeedFingerprint(seed64);
		// Default: WebZjs ZIP-32 SeedFingerprint (Blake2b of the seed).
		if (!this.webzjsReady) {
			configureWasm(webzjsWasm as unknown as Parameters<typeof configureWasm>[0]);
			await getWebZjsClient(DEFAULT_NETWORK).initialize();
			this.webzjsReady = true;
		}
		const SeedFingerprint = (webzjsWasm as unknown as { SeedFingerprint: new (s: Uint8Array) => { to_bytes(): Uint8Array } }).SeedFingerprint;
		const fp = new SeedFingerprint(seed64);
		return bytesToHex(fp.to_bytes());
	}

	/**
	 * Load a `.wult` share, derive the FROST UFVK / unified address and (if the
	 * share carries a vault identity) the PCZT seed fingerprint. Holds the
	 * decrypted share in memory for the tab lifetime; nothing is persisted.
	 */
	async loadShare(content: string, password?: string): Promise<LoadedCosignWallet> {
		const share = await unwrapVaultShare(content, password);
		if (!share.orchardFrost) {
			throw new Error('This share has no Orchard FROST key — Secresea co-signs Orchard (shielded ZEC) only.');
		}
		const bundle = toOrchardBundle(share.orchardFrost);
		const wasm = await this.ensureWasm();
		const addr = deriveOrchardAddressFromBundle(wasm, bundle);
		if (!addr) {
			throw new Error('Could not derive a Zcash address from this FROST share (incompatible or outdated WASM).');
		}

		let seedFingerprintHex: string | null = null;
		if (share.crossChainIdentity) {
			const seed = await deriveZcashSeedFromVaultIdentity(
				share.crossChainIdentity.hexChainCode,
				share.crossChainIdentity.publicKeyEcdsa,
			);
			seedFingerprintHex = await this.computeSeedFingerprint(seed);
		}

		this.bundle = bundle;
		this.loaded = {
			ufvk: addr.ufvk,
			unifiedAddress: addr.unifiedAddress,
			seedFingerprintHex,
			identifier: bundle.myShare.id,
			minSigners: Number(bundle.minSigners ?? 2),
			maxSigners: Number(bundle.maxSigners ?? 2),
		};
		return this.loaded;
	}

	/** Forget the loaded share + derived state. */
	forget(): void {
		this.bundle = null;
		this.loaded = null;
	}

	private scanner(): WalletScannerClientType {
		return this.deps.scannerClient ?? createWalletScannerClient({
			baseUrl: this.deps.scannerBaseUrl ?? DEFAULT_SCANNER_BASE_URL,
			apiKey: this.deps.scannerApiKey,
		});
	}

	/** Run a view-only witness scan of the FROST UFVK and return its notes. */
	private async scanNotes(birthdayHeight?: number, signal?: AbortSignal): Promise<ScannedNote[]> {
		if (!this.loaded) throw new Error('Load a co-signing share first.');
		const scanner = this.scanner();
		const start = await scanner.startOrchardScanJob({
			ufvk: this.loaded.ufvk,
			witness: true,
			autoDetect: birthdayHeight === undefined,
			...(birthdayHeight !== undefined ? { birthdayHeight } : {}),
		}, { apiKey: this.deps.scannerApiKey });
		const jobId = start.data?.jobId;
		const jobToken = start.data?.jobToken;
		if (!jobId) throw new Error('Scanner did not start a scan job.');

		const deadline = Date.now() + SCAN_TIMEOUT_MS;
		for (;;) {
			if (signal?.aborted) throw new Error('Scan cancelled.');
			if (Date.now() > deadline) throw new Error('Timed out scanning the wallet.');
			const status = await scanner.getOrchardScanJob(jobId, { jobToken, apiKey: this.deps.scannerApiKey });
			const job = status.data?.job;
			if (job?.status === 'succeeded') {
				return (job.results?.notes ?? []) as unknown as ScannedNote[];
			}
			if (job?.status === 'failed') {
				throw new Error(`Wallet scan failed: ${(job as { error?: string }).error ?? 'unknown error'}`);
			}
			await new Promise((r) => setTimeout(r, SCAN_POLL_MS));
		}
	}

	/** Scan the UFVK view-only and return the spendable balance. */
	async getBalance(birthdayHeight?: number, signal?: AbortSignal): Promise<CosignBalance> {
		const notes = await this.scanNotes(birthdayHeight, signal);
		const balanceZatoshis = notes.reduce((sum, n) => sum + Number(n.value || 0), 0);
		return { balanceZatoshis, balanceZec: toZec(balanceZatoshis), noteCount: notes.length };
	}

	/**
	 * Build, co-sign and broadcast a shielded send. Creates a WB32COSIGN session
	 * (QR via `onQrReady`), scans + builds the PCZT, then runs the per-action
	 * FROST ceremony with a WINBIT32 cosigner before broadcasting.
	 */
	async send(opts: CosignSendOptions): Promise<CosignSendResult> {
		if (!this.loaded || !this.bundle) throw new Error('Load a co-signing share first.');
		if (!opts.toAddress || !opts.toAddress.startsWith('u1')) {
			throw new Error('A unified (u1…) recipient address is required for shielded sends.');
		}
		const amountZat = toZatoshis(opts.amountZec);
		if (amountZat <= 0) throw new Error('Amount must be greater than zero.');

		const wasm = await this.ensureWasm();
		const { callbacks } = opts;

		// 1. Create the pairing session up-front so the cosigner can scan while we build.
		const relayUrl = this.config.relayBaseUrl;
		const { params, qrPayload } = createCosignSession(relayUrl);
		opts.onQrReady?.(qrPayload);

		const transport = (this.deps.createTransport
			? this.deps.createTransport(params.sessionId, params.encKeyHex)
			: createDefaultTransports({
				sessionId: params.sessionId,
				relayBaseUrl: relayUrl,
				fetchImpl: this.config.fetchImpl,
				// Secresea is always the ceremony initiator; this enables the
				// opportunistic peer-to-peer WebRTC leg (relay-signalled).
				myRole: 'init',
			}));

		try {
			// 2. Scan notes + select inputs.
			callbacks?.onProgress?.('Scanning your shielded notes...');
			const notes = await this.scanNotes(opts.birthdayHeight, callbacks?.signal);
			const { selected, feeZat } = selectNotes(notes, amountZat);
			const spends = selected.map(noteToSpend);

			// 3. Assemble outputs (+ change back to ourselves).
			const totalInZat = selected.reduce((s, n) => s + Number(n.value), 0);
			const changeZat = totalInZat - amountZat - feeZat;
			const outputs: Array<Record<string, unknown>> = [{
				toUnifiedAddress: opts.toAddress,
				valueZatoshis: amountZat,
				...(opts.memo ? { memoHex: bytesToHex(new TextEncoder().encode(opts.memo)) } : {}),
			}];

			// 4. Build the unsigned PCZT server-side.
			callbacks?.onProgress?.('Building the transaction...');
			const built = await buildPczt<{ pczt: string }>(this.config, {
				network: this.config.network,
				seedFingerprintHex: this.loaded.seedFingerprintHex ?? undefined,
				accountIndex: 0,
				ufvk: this.loaded.ufvk,
				feeZatoshis: feeZat,
				spends,
				outputs,
				...(changeZat > 0 ? { changeUnifiedAddress: this.loaded.unifiedAddress } : {}),
			});
			if (!built?.pczt) throw new Error('The server did not return a PCZT to sign.');

			// 5. Co-sign each Orchard action with the WINBIT32 cosigner, then broadcast.
			const result = await cosignSendPczt({
				config: this.config,
				wasm,
				encKeyHex: params.encKeyHex,
				transport,
				myPartyId: this.bundle.myShare.id,
				share: {
					id: this.bundle.myShare.id,
					secretShare: this.bundle.myShare.secretShare,
					publicKey: this.bundle.myShare.publicKey,
					groupPublic: this.bundle.publicKeyPackage.groupPublic,
				},
				publicKeyPackage: {
					signerPubkeys: this.bundle.publicKeyPackage.signerPubkeys,
					groupPublic: this.bundle.publicKeyPackage.groupPublic,
				},
				unsignedPczt: built.pczt,
				display: {
					chain: 'Zcash',
					ticker: 'ZEC',
					amount: opts.amountZec.toString(),
					toAddress: opts.toAddress,
					memo: opts.memo ?? '',
				},
				callbacks,
			});

			return { txid: result.txid, signedPczt: result.signedPczt };
		} finally {
			// Tell the cosigner we're done and release the transport.
			await endCosignSession(params.encKeyHex, transport).catch(() => {});
			try { transport.close?.(); } catch { /* best effort */ }
		}
	}
}

// ─── App-wide singleton ─────────────────────────────────────────────
//
// The loaded vault must outlive the co-sign modal so a loaded `.wult` stays
// "signed in" across the whole app — the header, the mint-owner address and
// co-signed mint payments all read the same instance. Tests inject their own
// `deps` and so always get a fresh, isolated service instead of this one.
let sharedCosignService: CosignWalletService | null = null;

export const getCosignWalletService = (): CosignWalletService => {
	if (!sharedCosignService) sharedCosignService = new CosignWalletService();
	return sharedCosignService;
};
